import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { toast } from "react-toastify";
import { clearDeclaration } from "../auth";

export default function LoginPage() {
  const navigate = useNavigate();
  const [form,        setForm]        = useState({ email: "", password: "" });
  const [loading,     setLoading]     = useState(false);
  const [showPwd,     setShowPwd]     = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Try jury login first
      try {
        const res = await api.post("/jury/login", form);
        const { token, jury } = res.data.data;
        localStorage.setItem("jury_token", token);
        localStorage.setItem("jury_user", JSON.stringify(jury));
        localStorage.setItem("jury_role", "jury");
        // A new session starts undeclared — the jury route guard shows the
        // declaration and the API refuses scores until it is accepted.
        clearDeclaration();
        toast.success(`Welcome, ${jury.name}`);
        navigate("/jury/events");
        return;
      } catch (juryErr) {
        if (!juryErr.response) throw juryErr;
      }

      // Admin login — no declaration needed
      const res  = await api.post("/admins/login", { emailId: form.email, password: form.password });
      const data = res.data.data;
      localStorage.setItem("jury_token", data.jwtToken);
      localStorage.setItem("jury_user", JSON.stringify(data));
      localStorage.setItem("jury_role", "admin");
      clearDeclaration();
      toast.success("Welcome back, Admin");
      navigate("/admin");
    } catch (err) {
      toast.error(!err.response ? "Network error — check your connection" : "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div style={{
        minHeight: "100vh",
        display: "flex",
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}>

        {/* ── Left panel ── */}
        <div style={{
          flex: "0 0 42%",
          background: "linear-gradient(145deg, #0F172A 0%, #1E293B 50%, #0F2744 100%)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "48px 52px",
          position: "relative",
          overflow: "hidden",
        }}>
          <div style={{ position: "absolute", top: -60, right: -60, width: 240, height: 240, borderRadius: "50%", background: "rgba(29,154,236,0.08)" }} />
          <div style={{ position: "absolute", bottom: 40, left: -80, width: 320, height: 320, borderRadius: "50%", background: "rgba(99,102,241,0.07)" }} />
          <div style={{ position: "absolute", top: "40%", right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(29,154,236,0.12)" }} />

          {/* Logo */}
          <div style={{ position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: "linear-gradient(135deg, #1D9AEC, #0EA5E9)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 800, fontSize: 22, color: "#fff",
                boxShadow: "0 4px 14px rgba(29,154,236,0.5)",
              }}>V</div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 18, color: "#fff", letterSpacing: "-0.03em" }}>Voice of Healthcare</div>
                <div style={{ fontSize: 11, color: "#64748B", letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 1 }}>Jury Management Portal</div>
              </div>
            </div>
          </div>

          {/* Hero text */}
          <div style={{ position: "relative" }}>
            <div style={{
              display: "inline-block",
              background: "rgba(29,154,236,0.15)", color: "#60C3F5",
              fontSize: 11, fontWeight: 600,
              padding: "5px 12px", borderRadius: 20,
              border: "1px solid rgba(29,154,236,0.2)",
              marginBottom: 20, letterSpacing: "0.05em",
            }}>JURY PANEL · 2025</div>

            <h1 style={{ fontSize: 34, fontWeight: 800, color: "#fff", lineHeight: 1.25, margin: "0 0 16px", letterSpacing: "-0.03em" }}>
              Empowering<br />
              <span style={{ color: "#1D9AEC" }}>Fair Recognition</span>
            </h1>
            <p style={{ fontSize: 14, color: "#64748B", lineHeight: 1.7, margin: 0, maxWidth: 300 }}>
              A unified platform for administrators and jury members to manage nominations, review submissions, and decide winners.
            </p>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 28 }}>
              {["Nomination Review", "Jury Scoring", "Winner Selection", "Email Notifications"].map(f => (
                <span key={f} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", color: "#94A3B8", fontSize: 12, fontWeight: 500, padding: "5px 12px", borderRadius: 20 }}>{f}</span>
              ))}
            </div>
          </div>

          <p style={{ position: "relative", fontSize: 12, color: "#334155", margin: 0 }}>
            © 2025 Voice of Healthcare Network
          </p>
        </div>

        {/* ── Right panel — form ── */}
        <div style={{
          flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
          background: "#F8FAFC", padding: "40px 32px",
        }}>
          <div style={{ width: "100%", maxWidth: 400 }}>

            <div style={{ marginBottom: 36 }}>
              <h2 style={{ fontSize: 24, fontWeight: 700, color: "#0F172A", margin: "0 0 8px", letterSpacing: "-0.03em" }}>
                Sign in to your account
              </h2>
              <p style={{ fontSize: 14, color: "#64748B", margin: 0 }}>
                Enter your credentials to access the jury portal
              </p>
            </div>

            <form onSubmit={handleSubmit}>

              {/* Email */}
              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Email address</label>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", fontSize: 15, color: "#94A3B8" }}>✉</span>
                  <input
                    type="email" required
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    className="voh-input"
                    style={{ paddingLeft: 38 }}
                  />
                </div>
              </div>

              {/* Password */}
              <div style={{ marginBottom: 28 }}>
                <label style={labelStyle}>Password</label>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", fontSize: 15, color: "#94A3B8" }}>🔒</span>
                  <input
                    type={showPwd ? "text" : "password"} required
                    placeholder="••••••••"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    className="voh-input"
                    style={{ paddingLeft: 38, paddingRight: 44 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(v => !v)}
                    style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: 13, fontWeight: 600, padding: 0 }}
                  >{showPwd ? "Hide" : "Show"}</button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%", padding: "12px",
                  background: loading ? "#94A3B8" : "linear-gradient(135deg, #1D9AEC, #1585D8)",
                  color: "#fff", border: "none", borderRadius: 8,
                  fontWeight: 700, fontSize: 15,
                  cursor: loading ? "not-allowed" : "pointer",
                  boxShadow: loading ? "none" : "0 4px 14px rgba(29,154,236,0.35)",
                  transition: "all 0.2s", letterSpacing: "-0.01em",
                }}
              >
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>

            <p style={{ textAlign: "center", color: "#94A3B8", fontSize: 12, marginTop: 24, lineHeight: 1.6 }}>
              Admin and jury members share the same login.<br />
              Contact your administrator if you've lost access.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

const labelStyle = { display: "block", fontSize: 12.5, fontWeight: 600, color: "#374151", marginBottom: 7 };
